/**
 * API クライアント (fetch ラッパ)
 *
 * Basic 認証ヘッダを zustand store から取得して全リクエストに付与する。
 * エラーレスポンスは ApiError に変換して上位に伝搬する。
 */
import { useAuthStore } from "@/store/auth";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type FetchOptions = RequestInit & {
  body?: BodyInit | Record<string, unknown> | null;
};

const API_BASE = "";

function authHeader(): Record<string, string> {
  const creds = useAuthStore.getState().credentials;
  if (!creds) return {};
  return { Authorization: `Basic ${creds}` };
}

async function parseError(resp: Response): Promise<ApiError> {
  let code = "unknown";
  let message = resp.statusText;
  try {
    const data = await resp.json();
    code = data.error || code;
    message = data.message || message;
  } catch {
    /* keep defaults */
  }
  return new ApiError(resp.status, code, message);
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...authHeader(),
    ...((opts.headers as Record<string, string>) ?? {}),
  };

  let body: BodyInit | null | undefined = opts.body as BodyInit | null;
  if (
    opts.body !== undefined &&
    opts.body !== null &&
    typeof opts.body === "object" &&
    !(opts.body instanceof FormData) &&
    !(opts.body instanceof Blob) &&
    !(opts.body instanceof ArrayBuffer)
  ) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const resp = await fetch(`${API_BASE}${path}`, { ...opts, body, headers });
  if (resp.status === 401) {
    // 認証切れ: ストアをクリア。AuthGate が /login にリダイレクトする。
    useAuthStore.getState().logout();
  }
  if (!resp.ok) throw await parseError(resp);

  const contentType = resp.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await resp.json()) as T;
  }
  return (await resp.blob()) as unknown as T;
}

export async function apiFetchBlob(
  path: string,
  opts: FetchOptions = {},
): Promise<{ blob: Blob; filename: string | null; headers: Headers }> {
  const headers: Record<string, string> = {
    ...authHeader(),
    ...((opts.headers as Record<string, string>) ?? {}),
  };

  let body: BodyInit | null | undefined = opts.body as BodyInit | null;
  if (
    opts.body !== undefined &&
    opts.body !== null &&
    typeof opts.body === "object" &&
    !(opts.body instanceof FormData)
  ) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const resp = await fetch(`${API_BASE}${path}`, { ...opts, body, headers });
  if (resp.status === 401) {
    useAuthStore.getState().logout();
  }
  if (!resp.ok) throw await parseError(resp);

  const dispo = resp.headers.get("content-disposition") || "";
  // RFC 5987 形式 (filename*=UTF-8''...) を優先、なければ古い filename= をフォールバック。
  // エアロジ CSV は日本語ファイル名 (例: "エアロジ出荷指示用_Shopee 分_20260611.csv")
  // を送るため UTF-8 デコードが必須 (2026-06-11)。
  let filename: string | null = null;
  const utf8Match = dispo.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      filename = decodeURIComponent(utf8Match[1]);
    } catch {
      filename = utf8Match[1];
    }
  } else {
    const m = dispo.match(/filename="?([^";]+)"?/);
    if (m) filename = m[1];
  }
  return { blob: await resp.blob(), filename, headers: resp.headers };
}
